import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, MenuItem, Paper, Switch, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import { WithholdingService } from '../../../services/withholding.service';
import { AccountService } from '../../../services/account.service';
import { AuthService } from '../../../services/auth.service';
import {
  WithholdingBasis, WithholdingRule, WithholdingRuleRequest, WithholdingSettings, WithholdingType,
} from '../../../interfaces/procurement/withholding.interface';
import { Account } from '../../../interfaces/accounting.interface';
import { AccountAutocomplete } from '../../common/AccountAutocomplete';
import { leafAccounts } from '../../../utils/postingAccounts';
import { useToast } from '../../../context/ToastContext';
import { usePermission } from '../../../hooks/usePermission';
import { PERMISSIONS } from '../../../constants/permissions';
import { formatMoney } from '../../../utils/format';

const NOTICE = 'Rates change with each Finance Act / SRO - verify every rate, slab and threshold before use.';
const BASES: Record<WithholdingType, WithholdingBasis[]> = { VDS: ['VAT_AMOUNT', 'VALUE'], TDS: ['VALUE', 'GROSS'] };
const BASIS_LABEL: Record<WithholdingBasis, string> = {
  VAT_AMOUNT: '% of the bill VAT', VALUE: '% of bill value (excl. VAT)', GROSS: '% of bill total (incl. VAT)',
};

const emptyRule = (): WithholdingRuleRequest => ({
  type: 'VDS', code: '', sectionRef: '', description: '', ratePct: 0, basis: 'VAT_AMOUNT', threshold: 0, active: true,
});

export const WithholdingSettingsPage: React.FC = () => {
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution()!;
  const canManage = usePermission(PERMISSIONS.ACCOUNT_MANAGE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<WithholdingSettings | null>(null);
  const [liabilities, setLiabilities] = useState<Account[]>([]);
  const [rules, setRules] = useState<WithholdingRule[]>([]);
  const [editing, setEditing] = useState<{ id?: string; form: WithholdingRuleRequest } | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const [s, r, accts] = await Promise.all([
        WithholdingService.getSettings(institutionId),
        WithholdingService.listRules(institutionId),
        AccountService.getAccountsByType('LIABILITY').catch(() => []),
      ]);
      setSettings(s);
      setRules(r ?? []);
      setLiabilities(leafAccounts(accts ?? []));
    } catch (e) {
      toast.showApiError(e, 'Failed to load withholding settings');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveSettings = async () => {
    if (!settings) return;
    try {
      setSaving(true);
      const saved = await WithholdingService.saveSettings(institutionId, {
        withholdingEntity: settings.withholdingEntity,
        vdsPayableAccountId: settings.vdsPayableAccountId || null,
        tdsPayableAccountId: settings.tdsPayableAccountId || null,
        entityBin: settings.entityBin ?? '',
        entityTin: settings.entityTin ?? '',
        signatoryName: settings.signatoryName ?? '',
        signatoryDesignation: settings.signatoryDesignation ?? '',
      });
      setSettings(saved);
      toast.showSuccess('Withholding settings saved');
    } catch (e) {
      toast.showApiError(e, 'Failed to save withholding settings');
    } finally {
      setSaving(false);
    }
  };

  const loadDefaults = async () => {
    try {
      const r = await WithholdingService.loadDefaults(institutionId);
      toast.showSuccess(`Loaded: ${r.created} new, ${r.updated} refreshed, ${r.skippedEdited} edited rows left untouched. ${r.notice}`);
      setRules(await WithholdingService.listRules(institutionId));
    } catch (e) {
      toast.showApiError(e, 'Failed to load Bangladesh defaults');
    }
  };

  const toggleActive = async (r: WithholdingRule) => {
    try {
      await WithholdingService.setRuleActive(institutionId, r.id, !r.active);
      setRules(await WithholdingService.listRules(institutionId));
    } catch (e) {
      toast.showApiError(e, 'Failed to update rule');
    }
  };

  const saveRule = async () => {
    if (!editing) return;
    try {
      if (editing.id) await WithholdingService.updateRule(institutionId, editing.id, editing.form);
      else await WithholdingService.createRule(institutionId, editing.form);
      setEditing(null);
      setRules(await WithholdingService.listRules(institutionId));
    } catch (e) {
      toast.showApiError(e, 'Failed to save rule');
    }
  };

  if (loading || !settings) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3, maxWidth: 1100 }} data-help-page="procurement.whRules.page">
      <Typography variant="h5" sx={{ mb: 2 }}>Withholding (VDS / TDS)</Typography>
      <Alert severity="warning" sx={{ mb: 2 }}>{NOTICE}</Alert>

      <Paper sx={{ p: 3, mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }} data-help-section="procurement.whRules.entity">
        <Typography variant="h6">Withholding entity</Typography>
        <FormControlLabel
          control={<Switch data-help="procurement.whSettings.field.entity" checked={settings.withholdingEntity} disabled={!canManage}
            onChange={e => setSettings({ ...settings, withholdingEntity: e.target.checked })} />}
          label="This institution deducts VAT / tax at source from supplier payments"
        />
        <AccountAutocomplete data-help="procurement.whSettings.field.vdsAccount" accounts={liabilities} value={settings.vdsPayableAccountId ?? ''} disabled={!canManage}
          onChange={id => setSettings({ ...settings, vdsPayableAccountId: id || null })}
          label="VDS payable account (liability)" placeholder="None" />
        <AccountAutocomplete data-help="procurement.whSettings.field.tdsAccount" accounts={liabilities} value={settings.tdsPayableAccountId ?? ''} disabled={!canManage}
          onChange={id => setSettings({ ...settings, tdsPayableAccountId: id || null })}
          label="TDS payable account (liability)" placeholder="None" />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <TextField data-help="procurement.whSettings.field.bin" label="BIN (shown on VDS certificates)" value={settings.entityBin ?? ''} disabled={!canManage}
            onChange={e => setSettings({ ...settings, entityBin: e.target.value })}
            sx={{ flex: '1 1 240px', maxWidth: 360 }} />
          <TextField data-help="procurement.whSettings.field.tin" label="TIN (shown on TDS certificates)" value={settings.entityTin ?? ''} disabled={!canManage}
            onChange={e => setSettings({ ...settings, entityTin: e.target.value })}
            sx={{ flex: '1 1 240px', maxWidth: 360 }} />
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <TextField data-help="procurement.whSettings.field.signatory" label="Certificate signatory name" value={settings.signatoryName ?? ''} disabled={!canManage}
            onChange={e => setSettings({ ...settings, signatoryName: e.target.value })}
            sx={{ flex: '1 1 240px', maxWidth: 360 }} />
          <TextField data-help="procurement.whSettings.field.designation" label="Signatory designation" value={settings.signatoryDesignation ?? ''} disabled={!canManage}
            onChange={e => setSettings({ ...settings, signatoryDesignation: e.target.value })}
            sx={{ flex: '1 1 240px', maxWidth: 360 }} />
        </Box>
        {canManage && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button data-help="procurement.whRules.save" variant="contained" onClick={saveSettings} disabled={saving}>Save settings</Button>
          </Box>
        )}
      </Paper>

      <Paper sx={{ p: 3 }} data-help-section="procurement.whRules.table">
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>Withholding rules</Typography>
          {canManage && (
            <>
              <Button data-help="procurement.whRules.defaults" variant="outlined" onClick={loadDefaults}>Load Bangladesh defaults</Button>
              <Button data-help="procurement.whRules.add" variant="contained" onClick={() => setEditing({ form: emptyRule() })}>Add rule</Button>
            </>
          )}
        </Box>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell data-help="procurement.whRules.col.type">Type</TableCell><TableCell data-help="procurement.whRules.col.code">Code</TableCell><TableCell data-help="procurement.whRules.col.rate">Rate</TableCell>
                <TableCell data-help="procurement.whRules.col.basis">Applied to</TableCell><TableCell data-help="procurement.whRules.col.threshold" align="right">Threshold</TableCell>
                <TableCell data-help="procurement.whRules.col.desc">Section / description</TableCell><TableCell data-help="procurement.whRules.col.status">Status</TableCell><TableCell data-help="procurement.whRules.col.actions" />
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.length === 0 && (
                <TableRow><TableCell colSpan={8}>No rules yet - load the Bangladesh defaults or add one.</TableCell></TableRow>
              )}
              {rules.map(r => (
                <TableRow key={r.id}>
                  <TableCell>{r.type}</TableCell>
                  <TableCell>{r.code}{r.edited && <Chip size="small" label="edited" sx={{ ml: 1 }} />}</TableCell>
                  <TableCell>{r.ratePct}%</TableCell>
                  <TableCell>{BASIS_LABEL[r.basis]}</TableCell>
                  <TableCell align="right">{r.threshold > 0 ? formatMoney(r.threshold) : 'always'}</TableCell>
                  <TableCell sx={{ maxWidth: 380 }}>
                    <Typography variant="body2">{r.description}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.sectionRef}</Typography>
                  </TableCell>
                  <TableCell>{r.active ? 'Active' : 'Inactive'}</TableCell>
                  <TableCell>
                    {canManage && (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button data-help="procurement.whRules.action.edit" size="small" onClick={() => setEditing({
                          id: r.id,
                          form: {
                            type: r.type, code: r.code, sectionRef: r.sectionRef ?? '', description: r.description ?? '',
                            ratePct: r.ratePct, basis: r.basis, threshold: r.threshold, active: r.active,
                          },
                        })}>Edit</Button>
                        <Button data-help="procurement.whRules.action.toggle" size="small" onClick={() => toggleActive(r)}>{r.active ? 'Deactivate' : 'Activate'}</Button>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      <Dialog open={editing !== null} onClose={() => setEditing(null)} fullWidth maxWidth="sm" data-help-page="procurement.whRuleForm.page">
        <DialogTitle>{editing?.id ? 'Edit rule' : 'Add rule'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {editing && (
            <>
              <TextField data-help="procurement.whRuleForm.field.type" select label="Type" value={editing.form.type} disabled={!!editing.id} sx={{ mt: 1 }}
                onChange={e => {
                  const type = e.target.value as WithholdingType;
                  setEditing({ ...editing, form: { ...editing.form, type, basis: BASES[type][0] } });
                }}>
                <MenuItem value="VDS">VDS (VAT deducted at source)</MenuItem>
                <MenuItem value="TDS">TDS (supplier income tax)</MenuItem>
              </TextField>
              <TextField data-help="procurement.whRuleForm.field.code" label="Code" value={editing.form.code}
                onChange={e => setEditing({ ...editing, form: { ...editing.form, code: e.target.value } })} />
              <TextField data-help="procurement.whRuleForm.field.sectionRef" label="Section / reference" multiline minRows={2} value={editing.form.sectionRef}
                onChange={e => setEditing({ ...editing, form: { ...editing.form, sectionRef: e.target.value } })} />
              <TextField data-help="procurement.whRuleForm.field.description" label="Description" value={editing.form.description}
                onChange={e => setEditing({ ...editing, form: { ...editing.form, description: e.target.value } })} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <TextField data-help="procurement.whRuleForm.field.rate" label="Rate (%)" type="number" value={editing.form.ratePct}
                  onChange={e => setEditing({ ...editing, form: { ...editing.form, ratePct: Number(e.target.value) } })}
                  inputProps={{ min: 0, max: 100, step: 'any' }} sx={{ flex: '1 1 140px', maxWidth: 200 }} />
                <TextField data-help="procurement.whRuleForm.field.basis" select label="Applied to" value={editing.form.basis}
                  onChange={e => setEditing({ ...editing, form: { ...editing.form, basis: e.target.value as WithholdingBasis } })}
                  sx={{ flex: '2 1 220px', maxWidth: 320 }}>
                  {BASES[editing.form.type].map(b => <MenuItem key={b} value={b}>{BASIS_LABEL[b]}</MenuItem>)}
                </TextField>
                <TextField data-help="procurement.whRuleForm.field.threshold" label="Threshold (bill value excl. VAT; 0 = always)" type="number" value={editing.form.threshold}
                  onChange={e => setEditing({ ...editing, form: { ...editing.form, threshold: Number(e.target.value) } })}
                  inputProps={{ min: 0, step: 'any' }} sx={{ flex: '1 1 200px', maxWidth: 280 }} />
              </Box>
              <FormControlLabel label="Active" control={<Switch data-help="procurement.whRuleForm.field.active" checked={editing.form.active}
                onChange={e => setEditing({ ...editing, form: { ...editing.form, active: e.target.checked } })} />} />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button data-help="procurement.whRuleForm.cancel" onClick={() => setEditing(null)}>Cancel</Button>
          <Button data-help="procurement.whRules.action.save" variant="contained" onClick={saveRule} disabled={!editing?.form.code.trim()}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WithholdingSettingsPage;
